<template>
  <v-container>
    <v-card class="px-6 py-6" flat>
      <div class="d-flex align-center mb-4">
        <h1>All Projects</h1>
        <v-spacer></v-spacer>
        <v-btn
          small
          outlined
          color="primary"
          class="mr-3"
          :to="`/pages/research/client/${$route.params.clientId}`"
        >
          <v-icon left small>mdi-arrow-left</v-icon>
          Back to Client Info
        </v-btn>
        <a
          :href="`/#/pages/research/client/${$route.params.clientId}/new-project`"
          class="ml-2"
        >
          <v-btn small color="primary" outlined>
            <v-icon left small>mdi-plus-box-outline</v-icon>
            Create New
          </v-btn>
        </a>
      </div>
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
        <div class="mt-4">Loading projects...</div>
      </div>

      <div v-else-if="sortedProjects.length === 0" class="text-center py-8">
        <v-icon size="64" color="grey lighten-1">mdi-folder-open</v-icon>
        <div class="mt-4 text-h6 grey--text">No projects found</div>
        <div class="text-body-2 grey--text">
          Create your first project to get started
        </div>
      </div>

      <div v-else>
        <div
          v-for="item in sortedProjects"
          :key="item.id"
          class="project-module mb-4"
        >
          <div class="d-flex align-center px-4 py-3">
            <div class="flex-grow-1">
              <div class="font-weight-bold text-h6 mb-1">
                <a
                  :href="`#/pages/projects/${item.id}/clusters`"
                  class="project-link"
                >
                  {{ item.name }}
                </a>
              </div>
              <div class="text-body-2 mb-2">
                {{ item.description || 'N/A' }}
              </div>
              <div class="mb-1">
                <span class="grey--text text--darken-1 mr-3">
                  {{ item.project_type || 'No type' }}
                </span>
                <v-chip
                  small
                  label
                  :class="{
                    flat: !isFailStatus(item) && !isSuccessStatus(item),
                    red: isFailStatus(item),
                    green: isSuccessStatus(item),
                  }"
                >
                  <span :class="{ 'text-white': isFailStatus(item) }">
                    {{ item.status || 'N/A' }}
                  </span>
                </v-chip>
              </div>
              <div class="text-caption grey--text">
                Last modified: {{ formatDate(item.updated_at) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
  export default {
    data() {
      return {
        projects_list: [],
        loading: true,
      }
    },
    computed: {
      sortedProjects() {
        if (!Array.isArray(this.projects_list)) {
          return []
        }
        return [...this.projects_list].sort((a, b) => {
          const dateA = new Date(a.updated_at || 0)
          const dateB = new Date(b.updated_at || 0)
          return dateB - dateA // Sort by most recent first
        })
      },
    },
    mounted() {
      this.apiGetProjectData()
    },
    methods: {
      apiGetProjectData() {
        this.loading = true
        this.apiGetClientProjects(this.$route.params.clientId)
          .then(response => {
            // Ensure response is an array
            this.projects_list = Array.isArray(response) ? response : []
          })
          .catch(error => {
            console.log(error)
            this.projects_list = []
          })
          .finally(() => {
            this.loading = false
          })
      },
      isFailStatus(project) {
        return (
          typeof project.status === 'string' &&
          project.status.toLowerCase().includes('fail')
        )
      },
      isSuccessStatus(project) {
        return (
          project.status === 'keywords_clustered' ||
          project.status === 'completed'
        )
      },
      formatDate(dateString) {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'N/A'

        const now = new Date()
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))
        const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))

        if (diffInHours < 0.5) {
          return 'Just now'
        } else if (diffInHours < 24) {
          return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`
        } else if (diffInDays < 7) {
          return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`
        } else {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        }
      },
    },
  }
</script>

<style scoped>
  /* Make status chips only take up content size */
  .v-chip {
    width: fit-content !important;
    display: inline-block !important;
  }

  .project-link {
    text-decoration: none;
    color: inherit;
  }

  .project-link:hover {
    text-decoration: underline;
  }

  .project-module {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    transition: box-shadow 0.2s ease;
  }

  .project-module:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  /* Status chip styling */
  .flat {
    background-color: #e0e0e0 !important;
    color: #333 !important;
  }

  .red {
    background-color: #f44336 !important;
    color: #fff !important;
  }

  .green {
    background-color: #43a047 !important;
    color: #fff !important;
  }

  .text-white {
    color: #fff !important;
  }
</style>
